<!-- Admin User Profile - Two Panel Layout -->
<div class="admin-user-container">
  
  <!-- Page Header -->
  <div class="page-header bg-white border-bottom shadow-sm mb-4 sticky-top">
    <div class="container-fluid py-3">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1 class="h3 mb-0 text-gray-800">
            <i class="fa fa-user-edit me-2 text-primary"></i>
            User Profile
          </h1>
          <p class="text-muted mb-0">
            <span *ngIf="user">{{user.firstName}} {{user.lastName}} - ID: {{user.id}}</span>
            <span *ngIf="!user">Loading user information...</span>
          </p>
        </div>
        <div class="d-flex gap-2">
          <a [routerLink]="['/backoffice/users']"
                 routerLinkActive="active"
                 [routerLinkActiveOptions]="{ exact: true }"
                 class="btn btn-outline-info btn-sm">
            <i class="fa fa-arrow-left me-1"></i>
            Back to Users
          </a>
          <button type="button" 
                  class="btn btn-outline-primary btn-sm"
                  (click)="refreshPage()"
                  [disabled]="isRefreshing">
            <i class="fa" 
               [class.fa-sync]="!isRefreshing" 
               [class.fa-spinner]="isRefreshing" 
               [class.fa-spin]="isRefreshing" 
               class="me-1"></i>
            {{ isRefreshing ? 'Refreshing...' : 'Refresh' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content Panel -->
  <div class="container-fluid pb-4" *ngIf="user">
    <div class="card border-0 shadow-sm">
      <div class="row g-0">
        
        <!-- Left Navigation Panel -->
        <div class="col-12 col-md-4 col-lg-3">
          <div class="h-100 border-end">
            
            <!-- User Info card -->
            <app-user-info-card [user]="user"></app-user-info-card>

            <!-- Navigation Links -->
            <app-admin-nav-menu 
              [activePanel]="activePanel" 
              (panelChange)="onPanelChange($event)">
            </app-admin-nav-menu>

          </div>
        </div>

        <!-- Right Content Panel -->
        <div class="col-12 col-md-8 col-lg-9">
          <div class="p-4">
            
            <!-- Personal Information Panel -->
            <div *ngIf="activePanel === 'personal'">

              <!-- Panel Header -->
              <app-panel-header 
                [title]="personalInfoConfig.title" 
                [icon]="personalInfoConfig.icon" 
                [editMode]="editPersonalMode" 
                [editButtonText]="personalInfoConfig.editButtonText"
                (editModeChange)="onPersonalEditModeChange($event)" 
                (save)="onPersonalInfoSave()">
              </app-panel-header>

              <!-- Personal Information Form -->
              <app-personal-info-form 
                *ngIf="user && countries && countries.length > 0"
                [user]="user" 
                [editMode]="editPersonalMode" 
                [userProfForm]="userProfForm" 
                [countries]="countries"
                [states]="states"
                [isUSSelected]="isUSSelected()"
                (saveChanges)="onPersonalInfoSave()"
                (editModeChange)="onPersonalEditModeChange($event)">
              </app-personal-info-form>

              <!-- Loading state for personal info form -->
              <div *ngIf="!user || !countries || countries.length === 0" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2 text-muted">Loading personal information form...</p>
              </div>

              <!-- Password Change Form -->
              <div class="mt-5">
                
                <app-panel-header 
                  [title]="passwordConfig.title" 
                  [icon]="passwordConfig.icon" 
                  [editMode]="editPasswordMode" 
                  [editButtonText]="passwordConfig.editButtonText"
                  (editModeChange)="onPasswordEditModeChange($event)" 
                  (save)="onPasswordSave()">
                </app-panel-header>

                <app-password-change-form 
                  [user]="user" 
                  [editMode]="editPasswordMode" 
                  [userPassForm]="userPassForm"
                  (saveChanges)="onPasswordSave()"
                  (editModeChange)="onPasswordEditModeChange($event)">
                </app-password-change-form>
              </div>
              
            </div>

            <!-- Course Information Panel -->
            <div *ngIf="activePanel === 'courses'">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h4 class="mb-0">
                  <i class="fa {{courseConfig.icon}} me-2 text-primary"></i>
                  {{courseConfig.title}}
                </h4>
                <div class="d-flex gap-2">
                  <!-- Certificate Generation Button -->
                  <button 
                    class="btn btn-outline-success btn-sm" 
                    (click)="onGenerateCertificate()"
                    [disabled]="isGeneratingCertificate || !user?.id"
                    title="Manually trigger certificate generation for this user">
                    <i class="fa" [ngClass]="{
                      'fa-spinner fa-spin': isGeneratingCertificate,
                      'fa-certificate': !isGeneratingCertificate
                    }" 
                    [attr.aria-hidden]="true"></i>
                    <span class="ms-1">
                      {{isGeneratingCertificate ? 'Generating...' : 'Generate Certificate'}}
                    </span>
                  </button>
                  
                  <!-- Edit Progress Button -->
                  <div *ngIf="!editMode; else editModeTemplate">
                    <button class="btn btn-primary btn-sm" (click)="onCourseEditModeChange(true)">
                      <i class="fa fa-edit me-1"></i>
                      {{courseConfig.editButtonText}}
                    </button>
                  </div>
                  <ng-template #editModeTemplate>
                    <div class="btn-group">
                      <button class="btn btn-success btn-sm" (click)="onCourseSave()">
                        <i class="fa fa-save me-1"></i>
                        Save Changes
                      </button>
                      <button class="btn btn-outline-secondary btn-sm" (click)="onCourseEditModeChange(false)">
                        <i class="fa fa-times me-1"></i>
                        Cancel
                      </button>
                    </div>
                  </ng-template>
                </div>
              </div>

              <app-course-progress-table 
                [userClasses]="userClassesTakenList" 
                [editMode]="editMode"
                (saveChanges)="onCourseSave()"
                (editModeChange)="onCourseEditModeChange($event)">
              </app-course-progress-table>
            </div>

            <!-- Account Activity Panel -->
            <div *ngIf="activePanel === 'activity'">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h4 class="mb-0">
                  <i class="fa fa-chart-line me-2 text-primary"></i>
                  <i class="fa fa-history"></i>
                  Account Activity
                </h4>
              </div>

              <!-- Placeholder for Account Activity 
              <div class="text-center py-5">
                <div class="mb-3">
                  <i class="fa fa-chart-line fa-3x text-muted"></i>
                </div>
                <p class="text-muted">
                  <b>Account Activity Coming Soon</b>
                </p>
                <p class="text-muted mb-0">
                  <i>This section will contain user login history, course progress analytics, 
                  and other account activity information.</i>
                </p>
                -->

                  <form>
                    <fieldset class="text-start" disabled>
                      <div class="row mb-3">
                        <div class="col-md-6">
                          <label for="disabled_admin_readonly_first_login" class="form-label">
                            <b>First Login</b>
                          </label>
                          <input type="text" id="disabled_admin_readonly_first_login" class="form-control" value="{{ user?.firstLogin | date:'MM/dd/yyyy hh:mm a' || 'NA' }}">
                        </div>
                        <div class="col-md-6">
                          <label for="disabled_admin_readonly_date_email_confirmed" class="form-label">
                            <b>Date Confirmed</b>
                          </label>
                          <input type="text" id="disabled_admin_readonly_date_email_confirmed" class="form-control" value="{{ user?.dateEmailConfirmed  | date:'MM/dd/yyyy hh:mm a' }}">
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-md-6">
                          <label for="disabled_admin_readonly_lastaccess" class="form-label">
                            <b>Last Access</b>
                          </label>
                          <input type="text" id="disabled_admin_readonly_lastaccess" class="form-control" value="{{ user?.lastAccess  | date:'MM/dd/yyyy' }}">
                        </div>
                        <div class="col-md-6">
                          <label for="disabled_admin_readonly_is_admin" class="form-label">
                            <b>Admin</b>
                          </label>
                          <input type="text" id="disabled_admin_readonly_is_admin" class="form-control" value="{{ user?.isAdmin === 1 }}">
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-md-6">
                          <label for="disabled_admin_readonly_is_in_class" class="form-label">
                            <b>In Class</b>
                          </label>
                          <input type="text" id="disabled_admin_readonly_is_in_class" class="form-control" value="{{ user?.isInClass === 1 }}">
                        </div>
                        <div class="col-md-6">
                          <label for="disabled_admin_readonly_email_confirmed" class="form-label">
                            <b>Email Confirmed</b>
                          </label>
                          <input type="text" id="disabled_admin_readonly_email_confirmed" class="form-control" value="{{ user?.emailConfirmed === 1 }}">
                        </div>
                      </div>
                    </fieldset>
                  </form>

              </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div class="container-fluid pb-4 text-center" *ngIf="!user">
    <div class="card border-0 shadow-sm">
      <div class="card-body p-5">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <h5 class="text-muted">Loading User Information...</h5>
        <p class="text-muted mb-0">Please wait while we fetch the user data.</p>
      </div>
    </div>
  </div>

</div>
